import { ffhClient } from './ffh-client';
import { datadogRumService } from '../datadogRum';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import type { TeamImportFailureStage } from '@lib/analytics/events';

// The OCR pipeline reports upstream failures (Vision API etc.) as a 502.
const OCR_FAILED_STATUS = 502;

export type { TeamImportFailureStage } from '@lib/analytics/events';

export class ImportTeamError extends Error {
  constructor(
    message: string,
    public readonly status: number | null,
    public readonly stage: TeamImportFailureStage
  ) {
    super(message);
    this.name = 'ImportTeamError';
  }
}

// Mirrors the mobile app's team_import_failed analytics event and the
// checkout addError pattern, so import failures land in both PostHog (product
// funnel, consent-gated) and Datadog RUM (diagnostics, always on) with the
// HTTP status and failure stage attached.
const reportImportFailure = (importError: ImportTeamError, cause: unknown) => {
  captureAnalyticsEvent(AnalyticsEvents.teamImportFailed, {
    import_method: 'screenshot',
    error_message: importError.message,
    failure_stage: importError.stage,
    status: importError.status
  });
  datadogRumService.addError(cause instanceof Error ? cause : importError, {
    context: 'team-import-screenshot',
    endpoint: '/import-team/screenshot',
    failureMode: importError.stage,
    status: importError.status
  });
};

export const importTeamFromScreenshot = async (base64Image: string) => {
  captureAnalyticsEvent(AnalyticsEvents.teamImportStarted, {
    import_method: 'screenshot'
  });

  try {
    const { data, error, response } = await ffhClient().POST(
      '/import-team/screenshot',
      {
        body: { base64Image }
      }
    );

    if (error || !data) {
      const status = response?.status ?? null;
      const stage: TeamImportFailureStage =
        status === null || status >= 500 ? 'server_error' : 'client_error';
      const message =
        status === OCR_FAILED_STATUS
          ? 'Screenshot processing failed. Please try again.'
          : 'Failed to import team from screenshot.';
      throw new ImportTeamError(message, status, stage);
    }

    captureAnalyticsEvent(AnalyticsEvents.teamImportCompleted, {
      import_method: 'screenshot'
    });
    return data.data;
  } catch (error) {
    const importError =
      error instanceof ImportTeamError
        ? error
        : new ImportTeamError(
            'Failed to import team from screenshot.',
            null,
            'network'
          );
    reportImportFailure(importError, error);
    throw importError;
  }
};
